import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const ReceiptCampaignDialog = ({
  t,
  open,
  onClose,
  isGenerating,
  isLoading,
  isError,
  campaigns,
  search,
  onSearchChange,
  selectedCampaignIds,
  onSelectAll,
  onClearAll,
  onToggleCampaign,
  selectionError,
  selectedYear,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onGenerate,
  isPreviewing = false,
  isActionDisabled = false,
  title,
  helperText,
  yearOptions,
  onYearChange,
  yearPrompt,
  actionLabel,
  warningText,
}) => {
  const filteredReceiptCampaigns = (campaigns || []).filter((campaign) =>
    String(campaign?.name || "")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={isGenerating || isPreviewing ? undefined : onClose}
    >
      <DialogTitle>
        {title ||
          t("donor_receipt_campaign_dialog_title", {
            fallback: "Select campaigns to include",
          })}
      </DialogTitle>
      <LoadingContainer loading={isLoading} error={isError}>
        <DialogContent>
          <div className="flex flex-1 flex-col gap-4">
            <Typography variant="body2">
              {helperText ||
                t("donor_receipt_campaign_dialog_helper", {
                  fallback:
                    "Only transactions from selected campaigns will be included in the receipt.",
                })}
            </Typography>

            {Array.isArray(yearOptions) && yearOptions.length > 0 && onYearChange ? (
              <div>
                {yearPrompt ? (
                  <Typography variant="body2" className="mb-1">
                    {yearPrompt}
                  </Typography>
                ) : null}
                <Select
                  fullWidth
                  size="small"
                  value={selectedYear}
                  onChange={(e) => onYearChange(e.target.value)}
                >
                  {yearOptions.map((yearOption) => (
                    <MenuItem value={yearOption} key={yearOption}>
                      {yearOption}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            ) : null}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DatePicker
                  label={t("donor_receipt_period_start", { fallback: "From" })}
                  value={startDate}
                  onChange={onStartDateChange}
                  maxDate={endDate || undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
                <DatePicker
                  label={t("donor_receipt_period_end", { fallback: "To" })}
                  value={endDate}
                  onChange={onEndDateChange}
                  minDate={startDate || undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </div>
            </LocalizationProvider>

            <TextField
              fullWidth
              size="small"
              label={t("donor_receipt_campaign_search_label", {
                fallback: "Search campaigns",
              })}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />

            <div className="flex items-center justify-between gap-2">
              <Typography variant="body2">
                {t("donor_receipt_campaign_selected_summary", {
                  selected: selectedCampaignIds.length,
                  total: campaigns?.length || 0,
                  fallback: `Selected ${selectedCampaignIds.length} of ${campaigns?.length || 0} campaigns`,
                })}
              </Typography>
              <div className="flex gap-2">
                <Button size="small" onClick={onSelectAll}>
                  {t("donor_receipt_campaign_select_all", {
                    fallback: "Select all",
                  })}
                </Button>
                <Button size="small" onClick={onClearAll}>
                  {t("donor_receipt_campaign_clear_all", {
                    fallback: "Clear all",
                  })}
                </Button>
              </div>
            </div>

            <div className="max-h-[360px] overflow-y-auto border rounded-md divide-y">
              {filteredReceiptCampaigns.length > 0 ? (
                filteredReceiptCampaigns.map((campaign) => {
                  const isSelected = selectedCampaignIds.includes(campaign._id);

                  return (
                    <button
                      key={campaign._id}
                      type="button"
                      className="w-full px-3 py-3 text-left flex items-center gap-3 hover:bg-gray-50"
                      onClick={() => onToggleCampaign(campaign._id)}
                    >
                      <Checkbox checked={isSelected} readOnly />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{campaign.name}</div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Chip
                            size="small"
                            color={campaign.enabled ? "success" : "default"}
                            label={
                              campaign.enabled
                                ? t("donor_receipt_campaign_status_active", {
                                    fallback: "Active",
                                  })
                                : t("donor_receipt_campaign_status_inactive", {
                                    fallback: "Inactive",
                                  })
                            }
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(
                              "donor_receipt_campaign_transactions_count",
                              {
                                count: campaign.transactions_count,
                                fallback: `${campaign.transactions_count} transactions`,
                              }
                            )}
                          />
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4">
                  <Typography variant="body2">
                    {campaigns?.length
                      ? t("donor_receipt_campaign_no_search_results", {
                          fallback: "No campaigns match your search.",
                        })
                      : t("donor_receipt_campaign_no_results_for_year", {
                          year: selectedYear,
                          fallback: `No campaigns with transactions were found for ${selectedYear}.`,
                        })}
                  </Typography>
                </div>
              )}
            </div>

            {selectionError ? (
              <Typography color="error" variant="body2">
                {selectionError}
              </Typography>
            ) : null}

            {warningText ? (
              <Typography color="error" variant="body2">
                {warningText}
              </Typography>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={onClose}
            disabled={isGenerating || isPreviewing}
          >
            {t("cancel")}
          </Button>
          <ButtonLoading
            loading={isGenerating || isPreviewing}
            disabled={isActionDisabled}
            variant="contained"
            onClick={onGenerate}
          >
            {actionLabel ||
              t("donor_receipt_preview_button", { fallback: "Preview receipt" })}
          </ButtonLoading>
        </DialogActions>
      </LoadingContainer>
    </Dialog>
  );
};

export default ReceiptCampaignDialog;
